import { useEffect, useId, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonGroup,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  Form,
  PageLoader,
  Stack,
  Text,
  TextArea,
  TextLabel,
  useConfirmationModal,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { PageTitle } from '@/components/page-title';
import { useServicesQuery, useServicesMutation, useQueryClient } from '@/services/api';
import { useAuth } from '@/services/auth';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import { useWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { isEmptyString } from '@/utils/string';
import { stripMdExtension, isContentOverLimit } from '../hooks/use-skills';
import { AgentLayout } from '../../layout';

export function SkillDetailPage() {
  const { skillPath } = useParams<{ skillPath: string }>();
  const { account } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();
  const safeNavigate = useSafeNavigate();
  const formId = useId();
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;

  const filePath = `skills/${skillPath ?? ''}`;
  const skillsListPath = generatePath('/workspaces/:workspaceId/agent/skills', { workspaceId });

  const fileQuery = useServicesQuery(
    'getCopilotVirtualFile',
    { params: { accountId: account.id, filePath } },
    { enabled: skillPath != null },
  );

  const [content, setContent] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);

  const file = fileQuery.data?.file;
  const displayName = stripMdExtension(skillPath ?? '');

  useEffect(() => {
    if (file != null && !hasInitialized) {
      setContent(file.content);
      setHasInitialized(true);
    }
  }, [file, hasInitialized]);

  const saveMutation = useServicesMutation('updateCopilotFile', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['listCopilotFiles'] });
      void queryClient.invalidateQueries({
        queryKey: ['getCopilotVirtualFile', { params: { accountId: account.id, filePath } }],
      });
      snackbars.create({ variant: 'success', message: 'Skill saved.' });
    },
    onError: () => {
      snackbars.create({ variant: 'error', message: 'Failed to save skill. Please try again.' });
    },
  });

  const deleteMutation = useServicesMutation('deleteCopilotFile', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['listCopilotFiles'] });
      snackbars.create({ variant: 'success', message: 'Skill deleted.' });
      safeNavigate(skillsListPath);
    },
    onError: () => {
      snackbars.create({ variant: 'error', message: 'Failed to delete skill. Please try again.' });
    },
  });

  const isContentTooLong = isContentOverLimit(content);
  const isContentEmpty = isEmptyString(content);
  const hasChanges = content !== (file?.content ?? '');
  const canSave =
    hasChanges && !isContentEmpty && !isContentTooLong && !saveMutation.isPending && !deleteMutation.isPending;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;

    saveMutation.mutate({
      params: { accountId: account.id, filePath },
      body: { content },
    });
  };

  const handleDelete = () => {
    confirm({
      title: 'Delete skill',
      message: 'Are you sure you want to delete this skill? This action cannot be undone.',
      isDanger: true,
      onConfirm() {
        deleteMutation.mutate({
          params: { accountId: account.id, filePath },
          body: {},
        });
      },
    });
  };

  if (fileQuery.isLoading) {
    return (
      <AgentLayout title="Skills">
        <PageTitle title="Skills" />
        <PageLoader label="Loading skill" />
      </AgentLayout>
    );
  }

  if (fileQuery.isError || file == null) {
    return (
      <AgentLayout title="Skills">
        <PageTitle title="Skills" />
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexGrow="1">
          <EmptyState>
            <EmptyStateTitle icon="error">Skill not found</EmptyStateTitle>
            <EmptyStateDescription>This skill may have been deleted or the path is invalid.</EmptyStateDescription>
          </EmptyState>
        </Box>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title={displayName}>
      <PageTitle title={`Skills — ${displayName}`} />
      <Box overflow="auto" flexGrow="1">
        <Box display="flex" flexDirection="column" gap="300" w="full" p="300">
          <Form id={formId} onSubmit={handleSubmit}>
            <Stack gap="200">
              <Stack gap="050">
                <TextLabel as="div">Name</TextLabel>
                <Text size="s">{displayName}</Text>
              </Stack>
              <Stack gap="050">
                <TextArea
                  label="Content"
                  value={content}
                  onInput={(event: Event & { target: HTMLTextAreaElement }) => setContent(event.target.value)}
                  minRows={8}
                  maxRows={24}
                  isInvalid={isContentTooLong}
                  error={isContentTooLong ? `Content exceeds maximum length of 1 MB` : undefined}
                  description="The instructions and knowledge this skill provides to the agent."
                />
              </Stack>
            </Stack>
          </Form>
          <Box display="flex" justifyContent="space-between">
            <Button
              variant="primary"
              size="sm"
              isDanger={true}
              onClick={handleDelete}
              isDisabled={saveMutation.isPending || deleteMutation.isPending}
            >
              Delete
            </Button>
            <ButtonGroup groupVariant="spaced">
              <Button
                variant="subtle"
                size="sm"
                href={skillsListPath}
                isDisabled={saveMutation.isPending || deleteMutation.isPending}
              >
                Back
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                form={formId}
                isDisabled={!canSave}
                isLoading={saveMutation.isPending}
              >
                Save
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Box>
    </AgentLayout>
  );
}
